import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';
import { AnimatePresence, motion } from 'framer-motion';

// Tooltip via React-Portal + position:fixed.
// - Hängt am document.body, wird also nicht durch overflow:hidden / Listen-Boxen abgeschnitten.
// - Berechnet seine Position relativ zum Trigger-Element (BoundingClientRect).
// - Klemmt sich an den Viewport-Rand: nie über den Bildschirmrand hinaus.
// - Bevorzugt oberhalb des Triggers; kippt nach unten, falls oben kein Platz ist.
const Tooltip = ({ text, children, className, tapToShow = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'top' });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const VIEWPORT_PADDING = 8; // Mindestabstand zum Bildschirmrand
  const GAP = 8;              // Abstand zwischen Trigger und Tooltip

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);
  const toggleTooltip = () => setIsVisible(v => !v);

  // Position berechnen, sobald Tooltip sichtbar ist und gerendert wurde.
  // useLayoutEffect, damit der erste Frame schon richtig sitzt (kein "Flackern" bei der Animation).
  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Default: oberhalb mittig
    let placement = 'top';
    let top = trigger.top - tooltip.height - GAP;
    let left = trigger.left + trigger.width / 2 - tooltip.width / 2;

    // Wenn oben nicht genug Platz: nach unten kippen.
    if (top < VIEWPORT_PADDING) {
      placement = 'bottom';
      top = trigger.bottom + GAP;
    }
    // Falls auch unten nicht passt: an Viewport klemmen.
    if (top + tooltip.height > vh - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, vh - tooltip.height - VIEWPORT_PADDING);
    }

    // Horizontal an Viewport klemmen
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (left + tooltip.width > vw - VIEWPORT_PADDING) {
      left = vw - tooltip.width - VIEWPORT_PADDING;
    }

    setCoords({ top, left, placement });
  }, [isVisible, text]);

  // Bei Scroll/Resize verschwindet der Tooltip - sonst würde er an alter Position kleben.
  useLayoutEffect(() => {
    if (!isVisible) return;
    const close = () => setIsVisible(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isVisible]);

  // Tap außerhalb schließt den Tooltip (nur wenn tapToShow aktiv).
  useLayoutEffect(() => {
    if (!tapToShow || !isVisible) return;
    const handleOutsideTap = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideTap);
    return () => document.removeEventListener('touchstart', handleOutsideTap);
  }, [tapToShow, isVisible]);

  const tooltipVariants = {
    hidden: {
      y: coords.placement === 'top' ? 4 : -4,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: { delay: 0.2 }
    }
  };

  const handleTouchStart = (e) => {
    e.stopPropagation();
    toggleTooltip();
  };

  return (
    <div
      ref={triggerRef}
      className={`tooltip-container ${className ?? ''}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onTouchStart={tapToShow ? handleTouchStart : undefined}
    >
      {children}
      {createPortal(
        <AnimatePresence>
          {isVisible && text !== '' && text != null && (
            <motion.div
              ref={tooltipRef}
              key={"tooltip"}
              className={`tooltip${tapToShow ? ' tooltip--tappable' : ''}`}
              style={{ top: coords.top, left: coords.left }}
              variants={tooltipVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {text}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
